import React, { useState } from 'react';
import { adoptionPct, formatWorkedDuration, toHours } from '../../utils/timeOnSystem';

/**
 * Adoption drives the colour everywhere in this view, so the thresholds live in
 * one place — the PDF report uses the same 60 / 25 split, and two sources of
 * truth for "is this tenant healthy" would eventually disagree.
 */
function toneClasses(pct) {
  if (pct >= 60) return { bar: 'bg-[#1c5f4c]', chip: 'bg-[#1c5f4c] text-white' };
  if (pct >= 25) return { bar: 'bg-amber-600', chip: 'bg-amber-600 text-white' };
  return { bar: 'bg-[#a5411d]', chip: 'bg-[#a5411d] text-white' };
}

function OverviewTile({ label, value, of, sub }) {
  return (
    <div className="flex-1 min-w-[170px] rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
        {value}
        {of != null ? (
          <span className="ml-1 text-base font-normal text-gray-500">/ {of}</span>
        ) : null}
      </div>
      {sub ? <div className="mt-0.5 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
}

/**
 * Cross-builder view: who on the platform is actually using the product.
 *
 * Bar length is time, colour is adoption. They are different questions — a big
 * builder can log a lot of hours while most of its people never sign in — so
 * showing only one of them would hide the case worth acting on.
 */
const TimeOnSystemOverview = ({ overview, isLoading, isError, onSelectBuilder }) => {
  const [expanded, setExpanded] = useState({});

  if (isLoading) {
    return (
      <div className="mt-5 rounded-lg border border-gray-200 bg-white py-10 text-center text-sm text-gray-500">
        Loading platform overview…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="mt-5 rounded-lg border border-gray-200 bg-white py-10 text-center text-sm text-red-600">
        Could not load the overview. Try again in a moment.
      </div>
    );
  }

  const builders = overview?.builders || [];
  const totals = overview?.totals;
  const maxMinutes = builders.reduce((m, b) => Math.max(m, b.total_minutes || 0), 0);

  return (
    <>
      <div className="flex flex-wrap gap-3">
        <OverviewTile
          label="Users active"
          value={totals?.active_users ?? 0}
          of={totals?.total_users ?? 0}
          sub={`${adoptionPct(totals?.active_users, totals?.total_users)}% of the user base`}
        />
        <OverviewTile
          label="Builders active"
          value={totals?.active_builders ?? 0}
          of={totals?.builders ?? 0}
          sub={`${totals?.companies ?? 0} companies in total`}
        />
        <OverviewTile
          label="Total time"
          value={`${toHours(totals?.total_minutes)} h`}
          sub="across the whole platform"
        />
        <OverviewTile
          label="Average per active user"
          value={formatWorkedDuration(
            totals?.active_users
              ? Math.round(totals.total_minutes / totals.active_users)
              : 0
          )}
          sub="excludes users who never opened it"
        />
      </div>

      <div className="mt-5 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-4 py-2.5 text-left font-medium">Builder</th>
              <th className="px-4 py-2.5 text-right font-medium">Active</th>
              <th className="px-4 py-2.5 text-right font-medium">Adoption</th>
              <th className="px-4 py-2.5 text-left font-medium">Time on system</th>
              <th className="px-4 py-2.5 text-right font-medium">Total</th>
              <th className="px-4 py-2.5 text-right font-medium" />
            </tr>
          </thead>
          <tbody>
            {!builders.length ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  No builders to show for this period.
                </td>
              </tr>
            ) : (
              builders.map((builder) => {
                const pct = adoptionPct(builder.active_users, builder.total_users);
                const tone = toneClasses(pct);
                const width =
                  maxMinutes > 0 && builder.total_minutes > 0
                    ? Math.max(1.5, (builder.total_minutes / maxMinutes) * 100)
                    : 0;
                const isOpen = !!expanded[builder.builder_id];

                return (
                  <React.Fragment key={builder.builder_id}>
                    <tr className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-gray-900">
                        {builder.builder_name}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                        {builder.active_users} / {builder.total_users}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${tone.chip}`}>
                          {pct}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="block h-2 w-full max-w-[220px] rounded-full bg-gray-100">
                          <span
                            className={`block h-2 rounded-full ${tone.bar}`}
                            style={{ width: `${width}%` }}
                          />
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                        {formatWorkedDuration(builder.total_minutes)}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() =>
                            setExpanded((prev) => ({
                              ...prev,
                              [builder.builder_id]: !prev[builder.builder_id]
                            }))
                          }
                          className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary">
                          {isOpen ? 'Hide companies' : 'Companies'}
                        </button>
                        <button
                          type="button"
                          onClick={() => onSelectBuilder?.(builder.builder_id)}
                          className="ml-1 rounded px-2 py-1 text-xs font-medium text-[#1c5f4c] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary">
                          Users
                        </button>
                      </td>
                    </tr>

                    {isOpen
                      ? (builder.companies || []).map((company) => {
                          const cPct = adoptionPct(
                            company.active_users,
                            company.total_users
                          );
                          return (
                            <tr
                              key={`${builder.builder_id}-${company.company_id ?? 'none'}`}
                              className="bg-gray-50/60">
                              <td className="py-2 pl-10 pr-4 text-gray-600">
                                {company.company_name}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                                {company.active_users} / {company.total_users}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                                {cPct}%
                              </td>
                              <td className="px-4 py-2" />
                              <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                                {formatWorkedDuration(company.total_minutes)}
                              </td>
                              <td />
                            </tr>
                          );
                        })
                      : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 max-w-3xl text-xs leading-relaxed text-gray-500">
        Bar length is total time; colour is adoption — the share of that builder&apos;s
        people who opened Housingram at all. They answer different questions: a large
        builder can log plenty of hours while most of its team never signs in, which
        is exactly the case worth acting on.
      </p>
    </>
  );
};

export default TimeOnSystemOverview;
